import objectPath from 'object-path'
import httpClient from 'utility/httpClient.util'
import { getApi } from './Api'
import { onCheckCondition } from './condition/Action'
import { applyRequestTimeout, withRequestTimeout } from './RequestTimeout'

const TABLE_GET_CACHE_TTL = 3000
const tableGetRequestCache = {}

const getTableGetRequestCacheData = (action, propTable, designContext) => {
  let requestUrl = getApi(
    action,
    {
      ...propTable,
      isTable: true,
      designContext,
    },
    'loadByGET',
  )
  let tableKey =
    objectPath.get(propTable, 'uischema.key') ||
    objectPath.get(propTable, 'uischema.name') ||
    action?.key ||
    action?.apiName ||
    ''
  let cacheKey = requestUrl ? [tableKey, requestUrl].join('|') : ''

  return { requestUrl, cacheKey }
}

const getTableGetCachedResultByKey = (cacheKey) => {
  if (!cacheKey) return null
  let cache = tableGetRequestCache[cacheKey]
  if (!cache) return null
  if (cache.status === 'pending') return cache
  if (Date.now() - cache.updatedAt > TABLE_GET_CACHE_TTL) {
    delete tableGetRequestCache[cacheKey]
    return null
  }
  return cache
}

export const getTableGetCachedResult = (action, propTable, designContext) => {
  if (propTable?.isRefreshTable || propTable?.isSearchRequest) return null
  let { cacheKey } = getTableGetRequestCacheData(
    action,
    propTable,
    designContext,
  )
  return getTableGetCachedResultByKey(cacheKey)
}

const getResponsePathValue = (response, path, defaultValue) => {
  if (!path) return defaultValue
  if (objectPath.has(response, path)) return objectPath.get(response, path)

  return defaultValue
}

const getResponseTotalCount = (response, pagination = {}, defaultValue) => {
  let totalCount = getResponsePathValue(
    response,
    pagination.totalCount,
    undefined,
  )
  totalCount = getResponsePathValue(response, pagination.total, totalCount)
  totalCount = getResponsePathValue(response, 'pagination.total', totalCount)

  return totalCount ?? defaultValue
}

export const setResponseTable = (response, action, propTable, loadData) => {
  let {
    pagination,
    formData,
    uischema,
    componentPropsData,
    search,
    onAction,
    elementTableColumn = [],
  } = propTable
  let { page, rowsPerPage } = pagination
  let {
    setLoading,
    setPage,
    setRowsPerPage,
    setTotalCount,
    setComponentPropsData,
    setFormData,
  } = onAction

  let tableData = []
  if (response) {
    let method = objectPath.get(loadData, 'method')
    let methodKey = method ? method.toLocaleLowerCase() : ''
    let dataMethod = objectPath.get(loadData, methodKey)
    let dataKey = objectPath.get(dataMethod, 'dataKey')
    let tableColumn = objectPath.get(uischema, 'tableColumn') || []
    let allColumn = []
    if (methodKey === 'search' && dataKey) {
      tableData = objectPath.get(response, dataKey) || []
      allColumn = objectPath.get(search, ['rawColumn', dataKey]) || []
      objectPath.set(action, 'enablePagination', dataMethod.enablePagination)
      objectPath.set(action, 'pagination', dataMethod.pagination)
    } else if (action.data) {
      tableData = objectPath.get(response, action.data) || []
      allColumn = objectPath.get(action, 'column') || []
    } else {
      tableData = response
      allColumn = objectPath.get(action, 'column') || []
    }

    if (!Array.isArray(tableData)) {
      tableData = [tableData]
    }
    allColumn = [...allColumn, ...tableColumn]

    let newList = []
    let start = page > 0 ? (page - 1) * rowsPerPage : 0
    tableData.map((data, index) => {
      let newData = {}
      allColumn.forEach(({ field }) => {
        let dataValue = objectPath.get(data, field)
        if (dataValue !== undefined) {
          objectPath.set(newData, field, dataValue)
        }
      })
      elementTableColumn.forEach((item) => {
        let { receiveKey, field } = item
        if (receiveKey) {
          let elementValue = objectPath.get(formData, receiveKey)
          objectPath.set(newData, field, elementValue)
        }
      })
      if (!objectPath.has(newData, 'id')) {
        objectPath.set(newData, 'id', start + index + 1)
      }
      objectPath.set(newData, 'tableData.no', start + index + 1)
      newList.push(newData)
    })
    tableData = newList
    setTotalCount(tableData.length)

    if (action.enablePagination && action.pagination) {
      let pagination = action.pagination
      if (pagination.page) {
        let page = getResponsePathValue(response, pagination.page, 0)
        page = page > 0 ? page - 1 : 0
        setPage(page)
      }
      if (pagination.rowsPerPage) {
        let newPerPage = getResponsePathValue(
          response,
          pagination.rowsPerPage,
          rowsPerPage ||
            uischema?.rowsPerPage ||
            5,
        )
        setRowsPerPage(newPerPage)
      }
      let totalCount = getResponseTotalCount(
        response,
        pagination,
        tableData.length,
      )
      setTotalCount(totalCount)
    }
    if (action.dataTable && action.dataTable.length > 0) {
      action.dataTable.forEach((table) => {
        let value = objectPath.get(response, table.response)
        let key = table.key
        objectPath.set(formData, key, value)
      })
      setFormData(formData)
      let data = objectPath.get(componentPropsData, 'data')
      if (data) {
        objectPath.set(componentPropsData, 'data', { ...data, ...formData })
        objectPath.set(componentPropsData, 'isResponseUpdated', true)
        setComponentPropsData(componentPropsData)
      }
    }
  } else {
    setPage(0)
    setTotalCount(0)
  }
  setLoading(false)
  return tableData
}

export const loadByGET = async (
  action,
  propTable,
  designContext,
  setIsRenderTable,
) => {
  let {
    onAction,
    uischema,
    searchPath,
    isRefreshTable,
    inputSearch,
    searchString,
    loading,
    fetchSearch,
    isSearchRequest,
  } = propTable
  let { setLoading, setTableData, setSearchPath, setSearchString } = onAction
  let loadData = objectPath.get(uischema, 'loadData') || {}
  const tableColumnList = objectPath.get(uischema, 'tableColumn') || []
  const elementTableColumn = tableColumnList.filter(
    ({ valueType }) => valueType === 'element',
  )
  const { jsonForm, setFieldFormData, setFieldSearchData } = designContext
  let formData = jsonForm.formData || {}

  let { requestUrl, cacheKey } = getTableGetRequestCacheData(
    action,
    propTable,
    designContext,
  )

  if (
    !isRefreshTable &&
    !isSearchRequest &&
    requestUrl === searchPath &&
    JSON.stringify(fetchSearch) === searchString
  ) {
    setLoading(false)
    return propTable.tableData
  }
  if (!isRefreshTable && !isSearchRequest) {
    let cachedResult = getTableGetCachedResultByKey(cacheKey)
    if (cachedResult) {
      if (cachedResult.promise) {
        let result = await cachedResult.promise
        setLoading(false)
        return result?.tableData || propTable.tableData
      }
      setLoading(false)
      return cachedResult.tableData || propTable.tableData
    }
  }
  if (setSearchPath) {
    setSearchPath(requestUrl)
  }
  if (setSearchString) {
    setSearchString(JSON.stringify(fetchSearch))
  }
  let response = []
  let tableData = []
  if (requestUrl) {
    let { headers = [] } = action
    let config = {}
    if (headers.length > 0) {
      headers.forEach(({ key, value }) => {
        if (key) {
          objectPath.set(config, `headers.${key}`, value)
        }
      })
    }
    config = applyRequestTimeout(config, action)
    let requestPromise = withRequestTimeout(
      httpClient.get(requestUrl, config),
      action,
    )
      .then(({ data }) => data)
      .catch(() => [])
    let tableResultPromise = requestPromise.then((responseData) => {
      let nextTableData = setResponseTable(
        responseData,
        action,
        propTable,
        loadData,
      )
      if (!isRefreshTable && !isSearchRequest && cacheKey) {
        tableGetRequestCache[cacheKey] = {
          status: 'resolved',
          tableData: nextTableData,
          updatedAt: Date.now(),
        }
      }
      return { response: responseData, tableData: nextTableData }
    })
    if (!isRefreshTable && !isSearchRequest && cacheKey) {
      tableGetRequestCache[cacheKey] = {
        status: 'pending',
        promise: tableResultPromise,
      }
    }
    let result = await tableResultPromise
    response = result.response
    tableData = result.tableData
  } else {
    tableData = setResponseTable(response, action, propTable, loadData)
  }
  console.log('SHOW TABLE', uischema.name, tableData)
  if (action.responseDataType === 'customData') {
    let customTable = action?.dataTable || []
    customTable.forEach((item) => {
      let dataKey = item.response
      let systemType = item.dataType
      let systemKey = item.systemKey
      let systemName = item.dynamicName

      if (systemType === 'input') {
        let value = objectPath.get(response, dataKey)
        setFieldFormData(systemKey, value)
        setFieldFormData(systemName, value)
        setFieldSearchData(uischema, systemKey, value)
        setFieldSearchData(uischema, systemName, value)
      }
    })
  }
  setFieldFormData(uischema.key, tableData)
  setFieldFormData(uischema.name, tableData)
  setTableData(tableData)
  setLoading(false)
  objectPath.set(formData, uischema.key, tableData)
  objectPath.set(formData, uischema.name, tableData)
  onCheckCondition(designContext, formData, uischema.key)
  if (setIsRenderTable) {
    setIsRenderTable(true)
    const { isUiChanged, setIsUiChanged } = designContext
    objectPath.set(isUiChanged, ['tableSelection', uischema.key], [])
    setIsUiChanged(isUiChanged)
  }
  return tableData
}

export const loadByPOST = async (action, propTable, designContext) => {
  let { onAction, uischema } = propTable
  let { setLoading, setTableData } = onAction
  let loadData = objectPath.get(uischema, 'loadData') || {}
  setLoading(true)
  let requestUrl = getApi(
    action,
    {
      ...propTable,
      isTable: true,
      designContext,
    },
    'loadByPOST',
  )
  let response = []
  if (requestUrl) {
    let { headers = [] } = action
    let config = {}
    if (headers.length > 0) {
      headers.forEach(({ key, value }) => {
        if (key) {
          objectPath.set(config, `headers.${key}`, value)
        }
      })
    }
    config = applyRequestTimeout(config, action)
    response = await withRequestTimeout(
      httpClient.post(requestUrl, {}, config),
      action,
    )
      .then(({ data }) => data)
      .catch(() => [])
  }
  let tableData = setResponseTable(response, action, propTable, loadData)
  setTableData(tableData)
  setLoading(false)
  return tableData
}

export const loadByJSON = (propTable, responseTable) => {
  let { pagination, onAction } = propTable
  let { setLoading, setTotalCount, setPage, setRowsPerPage } = onAction
  let { page, rowsPerPage } = pagination
  setPage(page)
  setRowsPerPage(rowsPerPage)
  setTotalCount(responseTable.length)
  setLoading(false)
}

export const renderRefreshTable = async (propTable) => {
  let { uischema, search, designContext } = propTable
  const tableColumnList = objectPath.get(uischema, 'tableColumn') || []
  const elementTableColumn = tableColumnList.filter(
    ({ valueType }) => valueType === 'element',
  )
  propTable = {
    ...propTable,
    elementTableColumn,
  }
  let tableData = []
  if (uischema?.loadData) {
    let { method, get, post } = uischema.loadData
    if (method === 'GET' && get) {
      tableData = await loadByGET(get, propTable, designContext)
    } else if (method === 'POST' && post) {
      tableData = await loadByPOST(post, propTable, designContext)
    } else if (method === 'SEARCH') {
      tableData = await loadByGET(search, propTable, designContext)
    }
  }
  return tableData
}

export const createColumn = (tableData) => {
  if (!tableData) return
  let columnData = tableData[0]
  if (!columnData) return
  let columnKey = Object.keys(tableData[0])
  let column = []
  let styleTable = {
    headerStyle: {
      minWidth: '150px',
      height: '40px',
      padding: '4px',
    },
    cellStyle: {
      minWidth: '150px',
      height: '40px',
      padding: '4px',
    },
  }
  if (!columnKey) return
  columnKey.forEach((item) => {
    if (columnData[item] && typeof columnData[item] === 'object') {
      if (Array.isArray(columnData[item])) {
        let dataValue = objectPath.get(columnData, item)
        let detailColumnData = dataValue[0]
        let detailColumnKey = Object.keys(dataValue[0])
        let detailColumn = []
        detailColumnKey.forEach((item) => {
          let dataValue = objectPath.get(detailColumnData, item)
          if (dataValue && typeof dataValue === 'object') {
            if (Array.isArray(dataValue)) {
              detailColumn.push({
                title: item,
                field: item,
                receiveData: `{${item}}`,
                value: '',
                type: 'array',
                valueType: 'receive',
                ...styleTable,
              })
            } else {
              let prefix = item
              getAllKeyFromObject(prefix, dataValue, detailColumn, styleTable)
            }
          } else {
            detailColumn.push({
              title: item,
              field: item,
              receiveData: `{${item}}`,
              value: '',
              type: 'string',
              valueType: 'receive',
              ...styleTable,
            })
          }
        })
        column.push({
          title: item,
          field: item,
          receiveData: `{${item}}`,
          detailColumn: detailColumn,
          type: 'array',
          typeField: 'detailPanel',
          value: '',
          valueType: 'receive',
          ...styleTable,
        })
      } else {
        let prefix = item
        let object = columnData[item]
        getAllKeyFromObject(prefix, object, column, styleTable, item)
      }
    } else {
      column.push({
        title: item,
        field: item,
        receiveData: `{${item}}`,
        value: '',
        type: 'string',
        valueType: 'receive',
        ...styleTable,
      })
    }
  })

  let newList = []
  tableData.map((data) => {
    let newData = {}
    column.forEach(({ field }) => {
      let dataValue = objectPath.get(data, field)
      if (Array.isArray(dataValue)) {
        objectPath.set(newData, field, JSON.stringify(dataValue))
      } else if (typeof dataValue === 'object') {
        dataValue = dataValue || {}
        let dataValueKeyList = Object.keys(dataValue)
        dataValueKeyList.forEach((key) => {
          let value = objectPath.get(dataValue, key)
          objectPath.set(newData, `${field}.${key}`, value)
          let checkMainColumn = column.findIndex((item) => item.field === field)
          if (checkMainColumn > -1) {
            column.splice(checkMainColumn, 1)
          }
          let checkSubColumn = column.find(
            (item) => item.field === `${field}.${key}`,
          )
          if (!checkSubColumn) {
            column.push({
              title: `${field}.${key}`,
              field: `${field}.${key}`,
              receiveData: `{${field}.${key}}`,
              value: '',
              type: 'string',
              valueType: 'receive',
              ...styleTable,
            })
          }
        })
      } else {
        objectPath.set(newData, field, dataValue)
      }
    })
    newList.push(newData)
  })
  return column
}

const getAllKeyFromObject = (key, object, column, styleTable, prefixKey) => {
  if (object) {
    let columnKey = Object.keys(object)
    columnKey.forEach((item) => {
      let prefix = `${key}.${item}`
      if (object[item] && typeof object[item] === 'object') {
        object = object[item]
        getAllKeyFromObject(prefix, object, column, styleTable, prefix)
      } else {
        column.push({
          prefixKey,
          title: prefix,
          field: prefix,
          receiveData: `{${prefix}}`,
          value: '',
          type: 'string',
          valueType: 'receive',
          ...styleTable,
        })
      }
    })
  }
}
